import { Badge, Button, Skeleton } from "@roberttamas/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Check, Loader2 } from "@/components/icons";
import { listTemplates, updateSiteConfig } from "@/lib/api";
import { toAppError } from "@/lib/errors";
import { CUSTOM_TEMPLATE_ID } from "@/lib/templates";
import type { Template } from "@/lib/types";
import { cn } from "@/lib/utils";

/** Palette keys shown as the mini swatch strip on each option. */
const SWATCH_KEYS = ["background", "primary", "secondary", "success", "error"];

interface TemplatePickerProps {
  siteId: string;
  templateId?: string;
}

/**
 * Applies a template to a site in one save (copying its palette + fonts and
 * stamping `templateId`). Highlights the active template, or shows a "Custom"
 * badge once the site's colors/fonts were hand-edited.
 */
export function TemplatePicker({ siteId, templateId }: TemplatePickerProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const invalidate = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["site", siteId] }),
      queryClient.invalidateQueries({ queryKey: ["sites"] }),
    ]);
  }, [queryClient, siteId]);

  const query = useQuery({ queryKey: ["templates"], queryFn: listTemplates });
  const templates = query.data?.templates ?? [];
  const activeId = templateId ?? CUSTOM_TEMPLATE_ID;

  const [pendingId, setPendingId] = useState<string | null>(null);
  const apply = useMutation({
    mutationFn: (template: Template) =>
      updateSiteConfig({
        siteId,
        configPatch: {
          colorScheme: {
            seeds: { seed: template.palette.primary },
            palette: template.palette,
          },
          fontScheme: template.fontScheme,
          templateId: template.id,
        },
      }),
    onSuccess: async (_res, template) => {
      toast.success(t("templatePicker.applied", { name: template.name }));
      await invalidate();
      setPendingId(null);
    },
    onError: (err) => {
      toast.error(toAppError(err).message);
      setPendingId(null);
    },
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="space-y-0.5">
          <p className="text-sm font-medium">{t("templatePicker.title")}</p>
          <p className="text-xs text-muted-foreground">
            {t("templatePicker.subtitle")}
          </p>
        </div>
        {activeId === CUSTOM_TEMPLATE_ID && <Badge variant="secondary">{t("templatePicker.customBadge")}</Badge>}
      </div>

      {query.isLoading && (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      )}

      {query.isError && (
        <div className="flex items-center justify-between gap-4 rounded-md border p-3 text-sm">
          <span className="text-destructive">{t("templates.loadFailed")}</span>
          <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
            {t("common.retry")}
          </Button>
        </div>
      )}

      {query.data && templates.length === 0 && (
        <p className="rounded-md border border-dashed px-3 py-6 text-center text-xs text-muted-foreground">
          {t("templatePicker.empty")}
        </p>
      )}

      {templates.length > 0 && (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {templates.map((template) => {
            const active = template.id === activeId;
            const busy = apply.isPending && pendingId === template.id;
            return (
              <button
                key={template.id}
                type="button"
                aria-pressed={active}
                disabled={apply.isPending}
                onClick={() => {
                  setPendingId(template.id);
                  apply.mutate(template);
                }}
                className={cn(
                  "rounded-lg border p-3 text-left transition-colors disabled:opacity-70",
                  active
                    ? "border-primary ring-1 ring-primary"
                    : "hover:bg-accent/40",
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-medium">{template.name}</span>
                  {busy ? (
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
                  ) : (
                    active && <Check className="h-4 w-4 shrink-0 text-primary" />
                  )}
                </div>
                <div className="mt-2 flex gap-1">
                  {SWATCH_KEYS.map((key) => (
                    <span
                      key={key}
                      className="h-5 w-5 rounded border border-border"
                      style={{ backgroundColor: template.palette[key] ?? "#000000" }}
                    />
                  ))}
                </div>
                <p className="mt-2 truncate text-[11px] text-muted-foreground">
                  {template.fontScheme.heading.family}, {template.fontScheme.body.family}
                </p>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
